import React, { type ReactElement, Dispatch } from "react";
import Downshift from "downshift";
import { usePreloadedQuery } from "react-relay";
import { ChevronIcon, CrossIcon, DropdownOption } from "@olivahealth/oli-ui";
import { GraphQLTaggedNode, OperationType } from "relay-runtime";
import { PreloadedQuery } from "react-relay/hooks";
import { sanitizeHtml } from "../../utils/sanitize";
import * as s from "./styles";
import type { SearchListboxVariant } from "./styles";

export interface Props {
  query: GraphQLTaggedNode;
  queryReference: PreloadedQuery<OperationType> | null | undefined;
  transformDataToOptions: (data: unknown) => DropdownOption<string>[];
  placeholder?: string;
  initialSelectedValue?: string;
  setSelected: Dispatch<DropdownOption<string> | null>;
  onSearchChange: Dispatch<string>;
  variant?: SearchListboxVariant;
}

export default function ServerSearchListbox({
  queryReference,
  query,
  transformDataToOptions,
  placeholder,
  initialSelectedValue,
  setSelected,
  onSearchChange,
  variant = "light",
}: Props): ReactElement {
  return (
    <Downshift
      onChange={setSelected}
      onInputValueChange={onSearchChange}
      initialSelectedItem={
        initialSelectedValue
          ? {
              id: initialSelectedValue,
              value: initialSelectedValue,
              label: initialSelectedValue,
            }
          : undefined
      }
      itemToString={(item) => (item ? item.value : "")}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        getToggleButtonProps,
        selectedItem,
        isOpen,
        openMenu,
        clearSelection,
      }) => (
        <div>
          <div className={s.inputBar(variant)}>
            <input
              {...getInputProps()}
              onClick={() => openMenu()}
              className={`h-8 w-full bg-transparent outline-none border-0 ${variant === "dark" ? "text-neutral-200 placeholder:text-neutral-500" : ""}`}
              placeholder={placeholder ?? ""}
            />
            {selectedItem ? (
              <button
                onClick={() => clearSelection()}
                aria-label="clear selection"
              >
                <CrossIcon color={variant === "dark" ? "white" : undefined} />
              </button>
            ) : (
              <button {...getToggleButtonProps()}>
                <ChevronIcon
                  direction="down"
                  className="mr-2 h-5 w-5"
                  aria-hidden="true"
                  size={20}
                  color={variant === "dark" ? "white" : undefined}
                />
              </button>
            )}
          </div>
          <div className="relative">
            <React.Suspense
              fallback={<p className={s.option(variant)}>Loading...</p>}
            >
              {isOpen && queryReference && (
                <RenderOptions
                  isOpen={isOpen}
                  transformDataToOptions={transformDataToOptions}
                  getMenuProps={getMenuProps}
                  getItemProps={getItemProps}
                  query={query}
                  queryReference={queryReference}
                  variant={variant}
                />
              )}
            </React.Suspense>
          </div>
        </div>
      )}
    </Downshift>
  );
}

interface RenderOptionsProps {
  getMenuProps: any;
  getItemProps: any;
  query: GraphQLTaggedNode;
  queryReference: PreloadedQuery<OperationType>;
  isOpen: boolean;
  transformDataToOptions: (data: unknown) => DropdownOption<string>[];
  variant: SearchListboxVariant;
}

function RenderOptions({
  getMenuProps,
  getItemProps,
  query,
  queryReference,
  isOpen,
  transformDataToOptions,
  variant,
}: RenderOptionsProps) {
  const data = usePreloadedQuery(query, queryReference);
  const options = transformDataToOptions(data);
  return (
    <ul {...getMenuProps()} className={s.optionsList(variant)} hidden={!isOpen}>
      {options.map((option, index) => (
        <li
          className={s.option(variant)}
          key={`${option.value}${index}`}
          {...getItemProps({
            item: option,
            index,
          })}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.label) }}
        />
      ))}
    </ul>
  );
}
